import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { Marquee } from "../components/Marquee";
import { ProjectViewer } from "../components/work/ProjectViewer";
import { ScrollTimecode } from "../components/ScrollTimecode";
import { SiteFooter } from "../components/SiteFooter";
import { SiteNavigation } from "../components/SiteNavigation";
import { WorkCard } from "../components/work/WorkCard";
import { DesignCard } from "../components/work/DesignCard";
import { useScrollIdle } from "../hooks/useScrollIdle";
import { archiveCategories } from "../data/archive";
import { designProjects, figmaFile } from "../data/design";
import { links, workApproach } from "../data/portfolio";
import { media, toolTicker } from "../data/media";
import { cardHeading, stageHeading } from "../utils/typography";
import type { WorkItem } from "../types/work";
export function VideosPage() {
  const [viewer, setViewer] = useState<WorkItem | null>(null);
  const [activeTab, setActiveTab] = useState(archiveCategories[0].id);
  const scrolling = useScrollIdle();
  const category = archiveCategories.find(entry => entry.id === activeTab) ?? archiveCategories[0];
  const isDesign = category.id === "product";
  const total = useMemo(() => archiveCategories.reduce((count, entry) => count + entry.items.length, 0) + designProjects.length, []);
  return <div id="top" className="min-h-screen w-full [overflow-x:clip] bg-[#050808] text-white selection:bg-[#54f6c6] selection:text-[#05100d]" data-id="element-603" data-mp="2ruxthqyqxc12">
      <ScrollTimecode data-id="element-604" data-mp="2n37hzho2oi82" />
      <SiteNavigation data-id="element-605" data-mp="2n2ein1rg0uzy" />

      <main data-id="element-606" data-mp="2n1wfrma9zij2">
        <header className="relative overflow-hidden border-b border-white/10 px-5 pb-16 pt-20 md:px-8 md:pb-20 md:pt-24" data-id="element-607" data-mp="2i5ea8k74fg5o">
          <img src={media.archivePlate} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-30" data-id="element-608" data-mp="3fks4s43f2sj7" />
          <div aria-hidden="true" className="absolute inset-0 bg-[linear-gradient(to_top,#050808_10%,rgba(5,8,8,0.65))]" data-id="element-609" data-mp="3asijx2h5sins" />
          <div aria-hidden="true" className="absolute inset-0 film-grain opacity-[0.18]" data-id="element-610" data-mp="3tseqi403jysy" />

          <div className="relative mx-auto max-w-[1480px]" data-id="element-611" data-mp="2llowz1e5xnw">
            <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-612" data-mp="3kwyyztmtdvqj">
              <Link to="/" data-cursor="Back" className="inline-flex min-h-11 items-center gap-3 font-mono text-[11px] uppercase tracking-[0.16em] text-white/55 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6]" data-id="element-613" data-mp="3oyk6ezgkxhtc">
                <ArrowLeft className="h-4 w-4" aria-hidden="true" data-id="element-614" data-mp="320pbkfq0vstv" /> Back to studio
              </Link>
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#54f6c6]" data-id="element-615" data-mp="360rdmpzo6o9o">
                Archive / {String(total).padStart(3, "0")} projects
              </span>
            </div>

            <div className="mt-12 text-center md:mt-16" data-id="element-616" data-mp="1470j47lsjwdg">
              <h1 className={`overflow-hidden ${stageHeading}`} data-id="element-617" data-mp="2os71rsys8j5p">
                <motion.span className="block" initial={{
                y: "110%"
              }} animate={{
                y: 0
              }} transition={{
                duration: 0.95,
                ease: [0.16, 1, 0.3, 1]
              }} data-id="element-618" data-mp="2kpk1r4ryjrxf">
                  All <span className="text-[#54f6c6]" data-id="element-619" data-mp="2yijj7cbavw7m">work</span>
                </motion.span>
              </h1>
              <p className="mx-auto mt-8 max-w-2xl text-[15px] leading-8 text-white/65 md:text-base" data-id="element-620" data-mp="2toxeq7faiymw">
                The full archive, grouped by discipline. Every frame plays its original source film.
              </p>
            </div>
          </div>
        </header>

        <div className="border-b border-white/10 py-6" data-id="element-621" data-mp="3hert4tf9cv99">
          <Marquee items={toolTicker} data-id="element-622" data-mp="3cgq8i426onvo" />
        </div>

        <section aria-label="Archive films" className="px-5 py-16 md:px-8 md:py-24" data-id="element-623" data-mp="3ci1bi1nt1rlq">
          <div className="mx-auto max-w-[1480px]" data-id="element-624" data-mp="1n2utweakql3n">
            <div role="tablist" aria-label="Work categories" className="flex flex-wrap gap-2" data-id="element-625" data-mp="1n3mq9rbzwmuz">
              {archiveCategories.map(entry => {
              const selected = entry.id === category.id;
              return <button key={entry.id} type="button" role="tab" id={`tab-${entry.id}`} aria-selected={selected} aria-controls={`panel-${entry.id}`} onClick={() => setActiveTab(entry.id)} data-cursor="Filter" className={`relative flex min-h-12 items-center gap-3 rounded-full border px-5 text-[13px] transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6] md:text-sm ${selected ? "border-[#54f6c6]/60 bg-[#54f6c6]/10 text-white" : "border-white/12 bg-white/[0.03] text-white/60 hover:border-white/25 hover:text-white"}`} data-id="element-626" data-mp="1oi7dla561jtm">
                    <span data-id="element-627" data-mp="figs962o44np">{entry.label}</span>
                    <span className="font-mono text-[11px] tracking-[0.14em] text-white/45" data-id="element-628" data-mp="fhfgt0hqpg5g">
                      {String(entry.id === "product" ? designProjects.length : entry.items.length).padStart(2, "0")}
                    </span>
                  </button>;
            })}
            </div>

            <div role="tabpanel" id={`panel-${category.id}`} aria-labelledby={`tab-${category.id}`} tabIndex={-1} className="focus-visible:outline-none" data-id="element-629" data-mp="kodig4y3kak">
              <div className="mt-10 flex flex-col gap-3 border-b border-white/10 pb-5 font-mono text-[11px] uppercase tracking-[0.16em] text-white/45 sm:flex-row sm:items-center sm:justify-between" data-id="element-630" data-mp="3iz725w1df1uo">
                <span className="text-[#54f6c6]" data-id="element-631" data-mp="390zzmi7twmc7">{category.caption}</span>
                <a href={isDesign ? figmaFile : links.sourceArchive} target="_blank" rel="noreferrer" data-cursor="Open" className="flex min-h-11 items-center gap-2 text-white/70 transition-colors hover:text-[#54f6c6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6]" data-id="element-632" data-mp="39081p0qrecir">
                  {isDesign ? "Open the Figma file" : "Open original archive"} <ExternalLink className="h-3 w-3" aria-hidden="true" data-id="element-633" data-mp="2qdqsgivdnn5f" />
                </a>
              </div>

              <p className="mt-6 max-w-2xl text-[15px] leading-8 text-white/65" data-id="element-634" data-mp="3e3l4iy7vh7om">{category.blurb}</p>

              {isDesign ? <div className="mt-12 grid gap-8 sm:grid-cols-2 xl:grid-cols-3" data-id="element-635" data-mp="3i5zr9tenwrl6">
                  {designProjects.map((project, index) => <motion.div key={project.id} initial={{
                opacity: 0,
                y: 28
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: "-8%"
              }} transition={{
                duration: 0.6,
                delay: Math.min(index * 0.05, 0.25),
                ease: [0.16, 1, 0.3, 1]
              }} data-id="element-636" data-mp="39arze98hrxc0" data-mpi={index}>
                      <DesignCard project={project} data-id="element-637" data-mp="azykygobpdil" />
                    </motion.div>)}
                </div> : (/* Columns keep vertical reels and landscape films in one flowing wall. */
            <div key={category.id} className="mt-12 gap-8 [column-fill:_balance] sm:columns-2 lg:columns-3 2xl:columns-4" data-id="element-638" data-mp="jtbhrtpicz9y">
                  {category.items.map((item, index) => <motion.div key={item.id} initial={{
                opacity: 0,
                y: 28
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: "-8%"
              }} transition={{
                duration: 0.6,
                delay: Math.min(index * 0.04, 0.24),
                ease: [0.16, 1, 0.3, 1]
              }} className="mb-8 break-inside-avoid" data-id="element-639" data-mp="3o79yx9lgy7fv" data-mpi={index}>
                      <WorkCard item={item} scrolling={scrolling} onOpen={setViewer} fitMedia loopOn="hover" data-id="element-640" data-mp="1cis6aga0bw0w" />
                    </motion.div>)}
                </div>)}
            </div>
          </div>
        </section>

        <section aria-labelledby="disciplines-heading" className="border-t border-white/10 px-5 py-16 md:px-8 md:py-24" data-id="element-641" data-mp="3fxcbbicnbgx5">
          <div className="mx-auto max-w-[1480px]" data-id="element-642" data-mp="3kp2ri4y9kywh">
            <div className="rounded-[20px] border border-white/10 bg-white/[0.03] p-7 md:p-10 lg:p-12" data-id="element-643" data-mp="3pnnta8t5zqn1">
              <h2 id="disciplines-heading" className={cardHeading} data-id="element-644" data-mp="3um8izeasirx8">
                {workApproach.heading.start} <span className="text-[#54f6c6]" data-id="element-645" data-mp="2ixvr7estrt8">{workApproach.heading.accent}</span>
              </h2>
              <ul className="mt-8 grid max-w-4xl gap-6" data-id="element-646" data-mp="7hizhgt9etmf">
                {workApproach.points.map(point => <li key={point.description} className="flex gap-4 sm:gap-5" data-id="element-647" data-mp="24auild6f2o3b">
                    <span aria-hidden="true" className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#54f6c6]" data-id="element-648" data-mp="298vy5o4mw5a1" />
                    <p className="text-[15px] leading-7 text-white/85 md:text-base md:leading-8" data-id="element-649" data-mp="21q4r999dvh1c">
                      {point.title && <span className="font-medium text-white" data-id="element-650" data-mp="1wxv7yc2rxf81">{point.title}. </span>}
                      <span className="text-white/70" data-id="element-651" data-mp="1rz1uukrefq8e">{point.description}</span>
                    </p>
                  </li>)}
              </ul>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter data-id="element-652" data-mp="2kn641g96ilfu" />
      <ProjectViewer item={viewer} items={category.items} onClose={() => setViewer(null)} onNavigate={setViewer} data-id="element-653" data-mp="2knpiuo3j8tpn" />
    </div>;
}